import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, History as HistoryIcon } from '@mui/icons-material';
import { Button, IconButton } from '@mui/material';
import {
	DataGridPro,
	GridColDef,
	GridFilterModel,
	GridPaginationModel,
	GridSortModel,
	GridToolbarColumnsButton,
	GridToolbarContainer,
	GridToolbarDensitySelector,
	GridToolbarFilterButton,
} from '@mui/x-data-grid-pro';
import { useMutation, useQuery } from '@parkchirp/api-client';
import { adaptGridValueCallback } from '@parkchirp/ui-library';
import Link from 'next/link';
import { ResourceTypes } from 'parkchirp-shared/enums';
import { useCallback, useContext, useEffect, useState } from 'react';
import { CommonContext } from '../../contexts';
import { useDebounce } from '../../hooks';
import { DELETE_HOURLY_ACCOUNT_V2, LIST_HOURLY_ACCOUNTS_V2 } from '../../queries';
import { datagridFilterPanelAttributes } from '../../utils/cypress';
import { AuditHistoryModal } from '../audit/auditHistoryModal';
import { DatagridLinearProgress } from '../loading/datagridLinearProgress';
import { ConfirmationInputModal } from '../modals/confirmationInputModal';

const defaultSortOrder = {
	sort: 'desc',
	field: 'pid',
};

const PortalAccountsToolbar = () => (
	<GridToolbarContainer>
		<GridToolbarFilterButton data-cy='filter-table-button' />
		<GridToolbarColumnsButton />
		<GridToolbarDensitySelector />
		<Link href='/portals/create' target='_blank'>
			<Button size='small' variant='outlined' startIcon={<AddIcon />}>
				New Portal Account
			</Button>
		</Link>
	</GridToolbarContainer>
);

export const PortalAccountsTable = () => {
	const { organizationId, setShowDialog, setShowSnackbar } = useContext(CommonContext);

	const [{ pageSize, page }, setPaginationModel] = useState<GridPaginationModel>({ pageSize: 100, page: 0 });
	const [rowCount, setRowCount] = useState<number>(0);
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(false);
	const [sortOrder, setSortOrder] = useState(defaultSortOrder);
	const [filters, setFilters] = useState([]);
	const debouncedSetFilters = useDebounce(setFilters, 500);

	const {
		data: hourlyAccountsData,
		loading: hourlyAccountsLoading,
		refetch: hourlyAccountsRefetch,
	} = useQuery(LIST_HOURLY_ACCOUNTS_V2, {
		variables: {
			limit: pageSize,
			offset: page * pageSize,
			filters,
			order: sortOrder,
			organizationId,
		},
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		fetchPolicy: 'network-only',
		ssr: false,
	});

	const [deleteHourlyAccount] = useMutation(DELETE_HOURLY_ACCOUNT_V2, {
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
	});

	useEffect(() => {
		if (!loading && hourlyAccountsData) {
			setRows(hourlyAccountsData?.listHourlyAccounts?.hourlyAccounts);
			setRowCount(hourlyAccountsData?.listHourlyAccounts?.count);
		}
	}, [loading, hourlyAccountsData]);

	const onFilterModelChange = useCallback(
		(filterModel: GridFilterModel) => {
			debouncedSetFilters(
				filterModel.items?.map((filterItem) => ({
					propertyName: filterItem.field,
					op: filterItem.operator,
					value: filterItem.value,
				})),
			);
		},
		[debouncedSetFilters],
	);

	const onSortModelChange = useCallback(
		(sortModel: GridSortModel) => {
			if (!sortModel.length) {
				return setSortOrder(defaultSortOrder);
			}

			const { field, sort } = sortModel[0];
			return setSortOrder({ field, sort });
		},
		[setSortOrder],
	);

	const handleDeleteAccount = useCallback(
		(account) => {
			const confirmationInputModal = (
				<ConfirmationInputModal
					title={`Are you sure you want to delete the account ${account.name}?`}
					validationString='DELETE'
					dismissText='Cancel'
					confirmText='Confirm'
					handleDismiss={() => setShowDialog(null)}
					handleConfirm={() =>
						deleteHourlyAccount({
							variables: {
								id: account.id,
							},
							onCompleted: () => {
								setShowDialog(null);
								setShowSnackbar({
									message: `Hourly account deleted successfully`,
									severity: 'success',
								});
								hourlyAccountsRefetch();
							},
							onError: (apolloError) => {
								setShowSnackbar({
									message: apolloError.message,
									severity: 'error',
								});
							},
						})
					}
				/>
			);
			setShowDialog(confirmationInputModal);
		},
		[deleteHourlyAccount, hourlyAccountsRefetch, setShowDialog, setShowSnackbar],
	);

	const handleAuditHistory = useCallback(
		(account) => {
			const dialog = (
				<AuditHistoryModal
					title='Hourly Portal Audit History'
					resourceId={String(account.id)}
					resourceType={ResourceTypes.HOURLY_ACCOUNT}
					onClose={() => setShowDialog(null)}
				/>
			);

			setShowDialog(dialog);
		},
		[setShowDialog],
	);

	const columns: GridColDef<any>[] = [
		{
			field: 'actions',
			headerName: 'Actions',
			width: 150,
			sortable: false,
			filterable: false,
			renderCell: (params) => {
				const actions = (
					<>
						<a href={`/portal-details/?accountId=${params.row.id}`} target='_blank' rel='noreferrer'>
							<IconButton>
								<EditIcon />
							</IconButton>
						</a>
						<IconButton onClick={() => handleDeleteAccount(params.row)}>
							<DeleteIcon />
						</IconButton>
						<IconButton onClick={() => handleAuditHistory(params.row)}>
							<HistoryIcon />
						</IconButton>
					</>
				);

				return actions;
			},
		},
		{
			field: 'pid',
			headerName: 'Portal Account ID',
			width: 150,
			type: 'number' as const,
			align: 'left' as const,
			headerAlign: 'left' as const,
			renderCell: (params) => (
				<a
					href={`/portal-details/?accountId=${params.row.id}`}
					target='_blank'
					rel='noreferrer'
					style={{ color: 'blue' }}
				>
					PID-{params.row.pid}
				</a>
			),
		},
		{
			field: 'facilityNumber',
			headerName: 'Facility #',
			width: 150,
			valueGetter: adaptGridValueCallback((params) => params.row.facility.facilityNumber),
		},
		{
			field: 'facilityName',
			headerName: 'Facility Name',
			width: 300,
			valueGetter: adaptGridValueCallback((params) => params.row.facility.facilityName),
		},
		{
			field: 'name',
			headerName: 'Portal Account Name',
			width: 300,
		},
		{
			field: 'organization',
			headerName: 'Organization',
			width: 150,
			filterable: false,
			sortable: false,
			valueGetter: adaptGridValueCallback((params) => params.row.organizationSettings.name),
		},
	];

	return (
		<div className='pca__datagrid-sticky--headfoot' data-cy='portals-datagrid-container'>
			<DataGridPro
				sx={{ width: 'auto', height: '100%', minHeight: '200px' }}
				rows={rows || []}
				rowCount={rowCount}
				columns={columns}
				loading={hourlyAccountsLoading}
				pagination
				pageSizeOptions={[pageSize]}
				paginationMode='server'
				paginationModel={{ page, pageSize }}
				onPaginationModelChange={(newModel) => {
					setLoading(true);
					setPaginationModel(newModel);
				}}
				sortingMode='server'
				onSortModelChange={onSortModelChange}
				filterMode='server'
				onFilterModelChange={onFilterModelChange}
				slots={{
					toolbar: PortalAccountsToolbar,
					loadingOverlay: DatagridLinearProgress,
				}}
				slotProps={{
					filterPanel: {
						sx: {
							'& .MuiDataGrid-filterFormValueInput': { width: 240 },
						},
						...datagridFilterPanelAttributes,
					},
				}}
			/>
		</div>
	);
};
